import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { Focus, Pencil, X } from 'lucide-react'
import type { MouseEvent, PointerEvent } from 'react'

const imageTileControlPanelClassName =
  'pointer-events-auto absolute right-2 top-2 z-30 inline-flex translate-y-0 items-center overflow-hidden rounded-xl bg-zinc-950/80 p-1 text-white opacity-100 shadow-[0_14px_34px_rgba(0,0,0,0.36)] ring-1 ring-white/10 backdrop-blur-md transition duration-150 md:pointer-events-none md:translate-y-1 md:opacity-0 md:group-hover:pointer-events-auto md:group-hover:translate-y-0 md:group-hover:opacity-100 md:group-focus-within:pointer-events-auto md:group-focus-within:translate-y-0 md:group-focus-within:opacity-100'

function stopActionPointerDown(event: PointerEvent<HTMLButtonElement>) {
  event.stopPropagation()
}

function stopActionClick(
  event: MouseEvent<HTMLButtonElement>,
  action: (() => void) | null | undefined
) {
  event.preventDefault()
  event.stopPropagation()
  action?.()
}

interface ImageTileControlsProps {
  canEdit?: boolean
  canAdjustFraming?: boolean
  canRemove?: boolean
  editLabel?: string
  removeLabel?: string
  onEdit?: (() => void) | null
  onAdjustFraming?: (() => void) | null
  onRemove?: (() => void) | null
  className?: string
}

export default function ImageTileControls({
  canEdit = false,
  canAdjustFraming = false,
  canRemove = false,
  editLabel = 'Edit image',
  removeLabel = 'Remove image',
  onEdit,
  onAdjustFraming,
  onRemove,
  className,
}: ImageTileControlsProps) {
  if (!canEdit && !canAdjustFraming && !canRemove) {
    return null
  }

  return (
    <div className={cn(imageTileControlPanelClassName, className)}>
      {canEdit ? (
        <Button
          variant="onDark"
          size="icon-sm"
          aria-label={editLabel}
          className="relative z-30"
          onPointerDown={stopActionPointerDown}
          onClick={(event) => stopActionClick(event, onEdit)}
        >
          <Pencil className="size-4" />
        </Button>
      ) : null}
      {canEdit && canAdjustFraming ? (
        <span aria-hidden="true" className="h-7 w-px bg-white/18" />
      ) : null}
      {canAdjustFraming ? (
        <Button
          variant="onDark"
          size="icon-sm"
          aria-label="Adjust storefront framing"
          className="relative z-30"
          onPointerDown={stopActionPointerDown}
          onClick={(event) => stopActionClick(event, onAdjustFraming)}
        >
          <Focus className="size-4" />
        </Button>
      ) : null}
      {(canEdit || canAdjustFraming) && canRemove ? (
        <span aria-hidden="true" className="h-7 w-px bg-white/18" />
      ) : null}
      {canRemove ? (
        <Button
          variant="onDark"
          size="icon-sm"
          aria-label={removeLabel}
          className="relative z-30"
          onPointerDown={stopActionPointerDown}
          onClick={(event) => stopActionClick(event, onRemove)}
        >
          <X className="size-4" />
        </Button>
      ) : null}
    </div>
  )
}
